import { useInfiniteQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { fetchUserPostsByType, fetchFeedPosts, fetchExplorePosts, fetchTrendingPosts, fetchReviewRequests } from '../services/postService';
import { fetchMyDrafts } from '../services/draftService';
import { fetchCustomPosts } from '../services/collectionService';

export const useUserPostsByType = (profileID, type, limit = 10, initialCursor = null) => {
  const { apiCall, isAuthenticated } = useAuth();
  
  return useInfiniteQuery({
    queryKey: ['userPosts', profileID, type, initialCursor],
    queryFn: ({ pageParam = initialCursor }) => {
      const isFirst = pageParam === initialCursor && initialCursor !== null;
      return fetchUserPostsByType(apiCall, profileID, type, limit, pageParam, isFirst);
    },
    initialPageParam: initialCursor, 
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!isAuthenticated && !!apiCall && !!profileID,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
};

export const useFeedPosts = (feedType = 'for_you', limit = 10) => {
  const { apiCall, isAuthenticated } = useAuth();
  
  return useInfiniteQuery({
    queryKey: ['feedPosts', feedType],
    queryFn: ({ pageParam = null }) => fetchFeedPosts(apiCall, feedType, limit, pageParam),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!isAuthenticated && !!apiCall,
    staleTime: 2 * 60 * 1000, 
    cacheTime: 5 * 60 * 1000, 
    retry: (failureCount, error) => {
      if (error?.status === 404 || error?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
};
export const useExplorePosts = (limit = 10) => {
  const { apiCall, isAuthenticated } = useAuth();

  return useInfiniteQuery({
    queryKey: ['explorePosts'],
    queryFn: ({ pageParam = null }) => {
      
      if (pageParam === null) {
        return fetchExplorePosts(apiCall, limit, null, null, []);
      } else {
        // Subsequent pages - use the cursors from pageParam
        return fetchExplorePosts(
          apiCall,
          limit,
          pageParam.trendingCursor,
          pageParam.randomCursor,
          pageParam.seenPostIds
        );
      }
    },
    getNextPageParam: (lastPage) => {
      // If no more posts, stop pagination
      if (!lastPage.hasMore) {
        return null;
      }

      // Return cursors for next page
      return {
        trendingCursor: lastPage.cursors.trendingCursor,
        randomCursor: lastPage.cursors.randomCursor,
        seenPostIds: lastPage._seenPostIds,
      };
    },
    enabled: !!isAuthenticated,
    staleTime: 2 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error?.status === 404 || error?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

export const useTrendingPosts = (limit = 10) => {
  const { apiCall, isAuthenticated } = useAuth();
  
  return useInfiniteQuery({
    queryKey: ['trendingPosts'], 
    queryFn: ({ pageParam = null }) => fetchTrendingPosts(apiCall, limit, pageParam),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!isAuthenticated && !!apiCall,
    staleTime: 5 * 60 * 1000, 
    cacheTime: 10 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error?.status === 404 || error?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

export const useReviewRequests = () => {
  const { apiCall, isAuthenticated } = useAuth();

  return useInfiniteQuery({
    queryKey: ['reviewRequests'],
    queryFn: ({ pageParam = null }) => fetchReviewRequests(apiCall, pageParam),
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextCursor : undefined,
    initialPageParam: null,
    enabled: !!isAuthenticated && !!apiCall,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error?.status === 404 || error?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

export const useMyDrafts = () => {
  const { apiCall, isAuthenticated } = useAuth();

  return useInfiniteQuery({
    queryKey: ['myDrafts'],
    queryFn: ({ pageParam = null }) => fetchMyDrafts(apiCall, pageParam),
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextCursor : undefined,
    initialPageParam: null,
    enabled: !!isAuthenticated && !!apiCall,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error?.status === 404 || error?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

export const useCollectionPosts = (collectionType, limit = 12, initialCursor = null) => {
  const { apiCall, isAuthenticated } = useAuth();
  
  return useInfiniteQuery({
    queryKey: ['collectionPosts', collectionType, initialCursor],
    queryFn: async ({ pageParam = initialCursor }) => {
      const isFirst = pageParam === initialCursor && initialCursor !== null;
      
      const response = await fetchCustomPosts(
        apiCall, 
        collectionType, 
        limit, 
        pageParam,
        isFirst  
      );

      return {
        posts: response.posts || [],
        nextCursor: response.nextCursor,
        hasMore: response.hasMore
      };
    },
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextCursor : undefined,
    initialPageParam: initialCursor,
    enabled: !!isAuthenticated && !!apiCall && !!collectionType,
    staleTime: 2 * 60 * 1000, 
    gcTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error?.status === 404 || error?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
};